/**
 * ============================================
 * STORAGE REDUX SLICE - NORMALIZED STATE
 * ============================================
 * STATE STRUCTURE:
 * {
 *   ids: [1, 2, 3, ...],           // Order matters for display
 *   entities: {                     // Fast lookup
 *     1: { id: 1, name: '...', ... },
 *     2: { id: 2, name: '...', ... },
 *   },
 *   pagination: {
 *     currentPage: 1,
 *     pageSize: 20,
 *     total: 1020,
 *     hasMore: true
 *   },
 *   filters: { search: '', type: '', status: '' },
 *   loading: false,
 *   loadingNextPage: false,
 *   error: null
 * }
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  StorageEntity,
  PaginatedResponse,
  FilterParams,
  FilterSelect,
  FilterOrderBy,
} from '@/pages/SettingPage/StorageManagement/types/types';

// ============================================
// STATE INTERFACE
// ============================================
interface StorageState {
  ids: string[];
  entities: Record<string, StorageEntity>;
  selectedIds: string[]; // IDs of selected drives for batch operations
  selectedMap: Record<string, StorageEntity>; // Cache selected entities for fast access
  pagination: {
    currentPage: number;
    pageSize: number;
    queryCount: number;
    pageCount: number;
    hasMore: boolean;
  };
  filters: FilterParams & {
    select?: FilterSelect;
    orderBy?: FilterOrderBy;
  };
  loading: boolean;
  loadingNextPage: boolean;
  error: string | null;
  optimisticUpdates: Record<string, { previousLimit: number | null }>;
  // Cache for search functionality
  cachedData: {
    ids: string[];
    entities: Record<string, StorageEntity>;
    pagination: {
      currentPage: number;
      pageSize: number;
      queryCount: number;
      pageCount: number;
      hasMore: boolean;
    };
  } | null;
  // Storage capacity validation
  capacityValidation: {
    isChecking: boolean;
    isExceeded: boolean;
    exceededType: 'USER' | 'COMPANY' | 'BOTH' | null;
    exceededBy: number;
    totalUploadSize: number;
    userRemaining: number;
    companyRemaining: number;
    message: string | null;
  };
}

const initialState: StorageState = {
  ids: [],
  entities: {},
  selectedIds: [],
  selectedMap: {},
  pagination: {
    currentPage: 0,
    pageSize: 20,
    queryCount: 0,
    pageCount: 0,
    hasMore: true,
  },
  filters: {
    search: '',
    type: '',
    status: '',
    select: 'usage',
    orderBy: 'desc',
  },
  loading: false,
  loadingNextPage: false,
  error: null,
  optimisticUpdates: {},
  cachedData: null,
  capacityValidation: {
    isChecking: false,
    isExceeded: false,
    exceededType: null,
    exceededBy: 0,
    totalUploadSize: 0,
    userRemaining: 0,
    companyRemaining: 0,
    message: null,
  },
};

// ============================================
// SLICE
// ============================================
const storageSlice = createSlice({
  name: 'storage',
  initialState,
  reducers: {
    // ========================================
    // FETCH INITIAL PAGE
    // ========================================
    fetchStorageEntitiesRequest(state, action: PayloadAction<FilterParams>) {
      state.loading = true;
      state.error = null;
      
      // Cache current data if search is being cleared (going from search to no search)
      const wasSearching = state.filters.search && state.filters.search.length > 0;
      const isNowSearching = action.payload.search && action.payload.search.length > 0;
      
      if (wasSearching && !isNowSearching && state.ids.length > 0) {
        // User cleared search - cache current data
        state.cachedData = {
          ids: [...state.ids],
          entities: { ...state.entities },
          pagination: { ...state.pagination },
        };
      } else if (isNowSearching && state.cachedData === null && state.ids.length > 0) {
        // User started searching - cache current data
        state.cachedData = {
          ids: [...state.ids],
          entities: { ...state.entities },
          pagination: { ...state.pagination },
        };
      }
      
      state.filters = action.payload;
      
      // Reset when filters change
      state.ids = [];
      state.entities = {};
      state.pagination.currentPage = 0;
    },

    fetchStorageEntitiesSuccess(
      state,
      action: PayloadAction<PaginatedResponse<StorageEntity>>
    ) {
      const { results, page, pageSize, queryCount, pageCount } = action.payload;

      state.loading = false;

      // Normalize: add entities
      results.forEach((entity) => {
        state.entities[entity.id] = entity;
      });

      // Add IDs (preserve order)
      state.ids = results.map((e) => e.id);

      // Update pagination
      state.pagination.currentPage = page;
      state.pagination.pageSize = pageSize;
      state.pagination.queryCount = queryCount;
      state.pagination.pageCount = pageCount;
      state.pagination.hasMore = page < pageCount;
      
      // Clear cache if we have new data and not searching
      if (!state.filters.search || state.filters.search.length === 0) {
        state.cachedData = null;
      }
    },

    fetchStorageEntitiesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // ========================================
    // FETCH NEXT PAGE (LAZY LOAD)
    // ========================================
    fetchNextPageRequest(state) {
      state.loadingNextPage = true;
      state.error = null;
    },

    fetchNextPageSuccess(
      state,
      action: PayloadAction<PaginatedResponse<StorageEntity>>
    ) {
      const { results, page, pageSize, queryCount, pageCount } = action.payload;

      state.loadingNextPage = false;

      // Normalize: add NEW entities (APPEND mode)
      results.forEach((entity) => {
        state.entities[entity.id] = entity;
      });

      // APPEND IDs (preserve order, no duplicates)
      const newIds = results.map((e) => e.id);
      state.ids = [...state.ids, ...newIds.filter((id) => !state.ids.includes(id))];

      // Update pagination
      state.pagination.currentPage = page;
      state.pagination.pageSize = pageSize;
      state.pagination.queryCount = queryCount;
      state.pagination.pageCount = pageCount;
      state.pagination.hasMore = page < pageCount;
    },

    fetchNextPageFailure(state, action: PayloadAction<string>) {
      state.loadingNextPage = false;
      state.error = action.payload;
    },

    // ========================================
    // UPDATE FILTERS (RESET TO PAGE 1)
    // ========================================
    updateFilters(state, action: PayloadAction<FilterParams>) {
      state.filters = { ...state.filters, ...action.payload };
      // Will trigger fetchStorageEntitiesRequest in component
    },

    // ========================================
    // OPTIMISTIC UPDATE (BATCH)
    // ========================================
    updateLimitOptimisticBatch(
      state,
      action: PayloadAction<{ entityIds: string[]; newLimitGB: number }>
    ) {
      const { entityIds, newLimitGB } = action.payload;
      const newLimitBytes = newLimitGB * 1024 * 1024 * 1024;

      console.log('🔄 OPTIMISTIC UPDATE START:', { entityIds, newLimitGB });

      entityIds.forEach((entityId) => {
        const entity = state.entities[entityId];
        if (entity) {
          console.log('📦 Before update:', entityId, {
            limitStorage: entity.limitStorage,
            usagePercentage: entity.usagePercentage
          });

          // Store previous value for rollback
          state.optimisticUpdates[entityId] = { previousLimit: entity.limitStorage };

          // Create new object to trigger React re-render (important!)
          state.entities[entityId] = {
            ...entity,
            limitStorage: newLimitBytes,
            lastUpdated: new Date().toISOString(),
            usagePercentage: (entity.storageUsed / newLimitBytes) * 100,
            isOverQuota: (entity.storageUsed / newLimitBytes) * 100 > 100,
          };

          console.log('✅ After update:', entityId, {
            limitStorage: state.entities[entityId].limitStorage,
            usagePercentage: state.entities[entityId].usagePercentage
          });
        }
      });

      console.log('🔄 OPTIMISTIC UPDATE END');
    },

    // ========================================
    // CONFIRM UPDATE (REMOVE OPTIMISTIC FLAG) - BATCH
    // ========================================
    updateLimitSuccessBatch(
      state,
      action: PayloadAction<{ updatedEntities: StorageEntity[] }>
    ) {
      const { updatedEntities } = action.payload;
      console.log('✅ SUCCESS BATCH:', updatedEntities.length, 'items');
      
      updatedEntities.forEach((updatedEntity) => {
        // Merge updated entity data while recalculating percentages
        const existingEntity = state.entities[updatedEntity.id];
        if (existingEntity) {
          console.log('✅ Confirming:', updatedEntity.id, {
            oldLimit: existingEntity.limitStorage,
            newLimit: updatedEntity.limitStorage
          });

          // Update all fields from API response
          // Remove optimistic flag
          delete state.optimisticUpdates[updatedEntity.id];
          
          // Update selectedMap with fresh data from API
          // if (state.selectedMap[updatedEntity.id]) {
          //   state.selectedMap[updatedEntity.id] = {
          //     ...state.entities[updatedEntity.id],
          //     ...updatedEntity,
          //   };
          // }
        }
      });
      
      // Only clear selection if all requested items were successful
      // (Check if any items still have optimistic updates - those are the failed ones)
      const hasFailedItems = state.selectedIds.some(id => state.optimisticUpdates[id]);
      if (!hasFailedItems) {
        state.selectedIds = [];
        state.selectedMap = {};
      }
    },

    // ========================================
    // ROLLBACK UPDATE - BATCH
    // ========================================
    updateLimitFailureBatch(
      state,
      action: PayloadAction<{ entityIds: string[]; error: string }>
    ) {
      const { entityIds, error } = action.payload;

      entityIds.forEach((entityId) => {
        const optimistic = state.optimisticUpdates[entityId];
        const entity = state.entities[entityId];

        if (entity && optimistic) {
          // Create new object for rollback to trigger React re-render
          const rolledBackEntity = {
            ...entity,
            limitStorage: optimistic.previousLimit,
            usagePercentage: optimistic.previousLimit !== null
              ? (entity.storageUsed / optimistic.previousLimit) * 100
              : 0,
            isOverQuota: optimistic.previousLimit !== null
              ? (entity.storageUsed / optimistic.previousLimit) * 100 > 100
              : false,
          };
          
          state.entities[entityId] = rolledBackEntity;

          // Also update selectedMap with rolled back data
          if (state.selectedMap[entityId]) {
            state.selectedMap[entityId] = rolledBackEntity;
          }

          delete state.optimisticUpdates[entityId];
        }
      });

      // Remove only the failed items from selection
      entityIds.forEach((id) => {
        state.selectedIds = state.selectedIds.filter(selectedId => selectedId !== id);
        delete state.selectedMap[id];
      });
      
      state.error = error;
    },

    // ========================================
    // SELECTION MANAGEMENT
    // ========================================
    toggleSelectEntity(state, action: PayloadAction<string>) {
      const id = action.payload;
      const index = state.selectedIds.indexOf(id);
      if (index > -1) {
        // Deselect: remove from both selectedIds and selectedMap
        state.selectedIds.splice(index, 1);
        delete state.selectedMap[id];
      } else {
        // Select: add to selectedIds and cache entity in selectedMap
        const entity = state.entities[id];
        if (entity) {
          state.selectedIds.push(id);
          state.selectedMap[id] = entity;
        }
      }
    },

    clearSelection(state) {
      state.selectedIds = [];
      state.selectedMap = {};
    },

    removeFromSelection(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.selectedIds = state.selectedIds.filter((id) => id !== action.payload);
      delete state.selectedMap[id];
    },

    // ========================================
    // NEW ACTIONS FOR EPIC INTEGRATION
    // ========================================
    initStorageManagement(
      _state,
      _action: PayloadAction<{ filters?: FilterParams } | undefined>
    ) {
      // This action triggers the epic to fetch initial data
    },

    applyFilters(state, action: PayloadAction<FilterParams>) {
      state.filters = { ...state.filters, ...action.payload };
      // This action triggers the epic to refetch
    },

    resetFilters(state) {
      state.filters = initialState.filters;
      // This action triggers the epic to refetch with defaults
    },

    // ========================================
    // RESTORE CACHED DATA
    // ========================================
    restoreCachedData(state) {
      if (state.cachedData) {
        state.ids = state.cachedData.ids;
        state.entities = state.cachedData.entities;
        state.pagination = state.cachedData.pagination;
        state.cachedData = null;
        state.loading = false;
      }
    },

    // ========================================
    // RESET STATE
    // ========================================
    resetStorageState() {
      return initialState;
    },

    // ========================================
    // STORAGE CAPACITY VALIDATION
    // ========================================
    checkStorageCapacityBeforeUpload(
      state,
      _action: PayloadAction<{
        totalUploadSize: number;
        isTeamDrive: boolean;
        files?: File[];
        uploadContext?: {
          uploadType: 'file' | 'folder';
          uploadPayload: any;
        };
      }>
    ) {
      state.capacityValidation.isChecking = true;
      state.capacityValidation.isExceeded = false;
      state.capacityValidation.message = null;
    },

    storageCapacityCheckSuccess(
      state,
      action: PayloadAction<{
        totalUploadSize: number;
        userRemaining: number;
        companyRemaining: number;
      }>
    ) {
      state.capacityValidation.isChecking = false;
      state.capacityValidation.isExceeded = false;
      state.capacityValidation.totalUploadSize = action.payload.totalUploadSize;
      state.capacityValidation.userRemaining = action.payload.userRemaining;
      state.capacityValidation.companyRemaining = action.payload.companyRemaining;
    },

    storageCapacityExceeded(
      state,
      action: PayloadAction<{
        exceededType: 'USER' | 'COMPANY' | 'BOTH';
        exceededBy: number;
        totalUploadSize: number;
        userRemaining: number;
        companyRemaining: number;
        message: string;
      }>
    ) {
      state.capacityValidation.isChecking = false;
      state.capacityValidation.isExceeded = true;
      state.capacityValidation.exceededType = action.payload.exceededType;
      state.capacityValidation.exceededBy = action.payload.exceededBy;
      state.capacityValidation.totalUploadSize = action.payload.totalUploadSize;
      state.capacityValidation.userRemaining = action.payload.userRemaining;
      state.capacityValidation.companyRemaining = action.payload.companyRemaining;
      state.capacityValidation.message = action.payload.message;
    },

    clearStorageCapacityError(state) {
      state.capacityValidation = initialState.capacityValidation;
    },

    // Action to proceed with upload after capacity check passes
    proceedUpload(
      _state,
      _action: PayloadAction<{
        uploadContext: {
          uploadType: 'file' | 'folder';
          uploadPayload: any;
        };
      }>
    ) {
      // This action is intercepted by uploadInterceptEpic
      // Epic will dispatch the appropriate upload action
    },
  },
});

// ============================================
// EXPORTS
// ============================================
export const storageActions = storageSlice.actions;
export default storageSlice.reducer;

// ============================================
// SELECTORS
// ============================================

// Select all entities as array (denormalized for display)
export const selectStorageEntities = (state: { storage: StorageState }) =>
  state.storage.ids
    .map((id) => state.storage.entities[id])
    .filter((entity) => entity?.type !== "company");

// Select single entity by ID (O(1) lookup)
export const selectStorageEntityById = (state: { storage: StorageState }, id: string) =>
  state.storage.entities[id];

// Select pagination info
export const selectPagination = (state: { storage: StorageState }) =>
  state.storage.pagination;

// Select filters
export const selectFilters = (state: { storage: StorageState }) =>
  state.storage.filters;

// Select loading states
export const selectLoading = (state: { storage: StorageState }) =>
  state.storage.loading;

export const selectLoadingNextPage = (state: { storage: StorageState }) =>
  state.storage.loadingNextPage;

// Select error
export const selectError = (state: { storage: StorageState }) =>
  state.storage.error;

// Check if entity is being optimistically updated
export const selectIsOptimistic = (state: { storage: StorageState }, id: string) =>
  !!state.storage.optimisticUpdates[id];

// Select selected entity IDs
export const selectSelectedIds = (state: { storage: StorageState }) =>
  state.storage.selectedIds;

// Select selected entities map (cached data)
export const selectSelectedMap = (state: { storage: StorageState }) =>
  state.storage.selectedMap;

// Select selected entities as array (use selectedMap for cached data)
export const selectSelectedEntities = (state: { storage: StorageState }) =>
  state.storage.selectedIds.map((id) => state.storage.selectedMap[id]).filter(Boolean);

// Select capacity validation state
export const selectCapacityValidation = (state: { storage: StorageState }) =>
  state.storage.capacityValidation;
